// src/contexts/AuthContext.jsx
// Contexto de Autenticación para toda la aplicación

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import authService from '../firebase/auth';
import { applyEdusicoSsoFromUrl } from '../firebase/edusicoSso';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { clearLegacyProfesorId } from '../firebase/profesorId';
import { ensureUserStorageWorkspace } from '../services/storageWorkspaceService';
import { clearBordonaSessionCache } from '../bordona/lib/clearBordonaSessionCache';

/**
 * 🔐 CONTEXTO DE AUTENTICACIÓN
 * 
 * Provee el estado de autenticación a toda la aplicación
 * Maneja automáticamente la persistencia de sesión
 * 
 * USO:
 * const { user, loading, login, logout } = useAuth();
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessProfile, setAccessProfile] = useState(null);
  const [profesorData, setProfesorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const previousUidRef = useRef(null);

  const checkUserAccess = async (authUser) => {
    const userRef = doc(db, 'users', authUser.uid);
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      return { id: snap.id, ...snap.data() };
    }

    // Cuentas legacy: ficha en `profesores` pero sin documento en `users`.
    // Sin `users`, las reglas de Firestore bloquean cursos/sesiones/unidades.
    let profesorSnap = null;
    try {
      profesorSnap = await getDoc(doc(db, 'profesores', authUser.uid));
    } catch (e) {
      console.warn('⚠️ No se pudo leer profesores al bootstrap de users:', e);
    }

    const fromProfesor = profesorSnap?.exists() ? profesorSnap.data() : null;
    const isStudentAccount = !fromProfesor;
    const displayName =
      fromProfesor?.nombreCompleto ||
      [fromProfesor?.nombre, fromProfesor?.apellido].filter(Boolean).join(' ').trim() ||
      authUser.displayName ||
      (isStudentAccount ? 'Estudiante' : 'Profesor');

    const newUser = {
      email: authUser.email || fromProfesor?.email || null,
      displayName,
      photoURL: authUser.photoURL || fromProfesor?.fotoPerfil || null,
      accessStatus: fromProfesor ? 'active' : 'active',
      role: 'user',
      accountType: fromProfesor ? 'profesor' : 'estudiante',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, newUser, { merge: false });
    console.log(
      fromProfesor
        ? '✅ Perfil users creado desde profesores (cuenta legacy)'
        : '⏳ Perfil users creado en lista de espera'
    );
    return { id: authUser.uid, ...newUser };
  };

  // Observador de estado de autenticación
  useEffect(() => {
    console.log('🔐 Inicializando AuthContext...');

    let cancelled = false;
    let unsubscribe = () => {};

    const boot = async () => {
      setLoading(true);

      try {
        await authService.initPersistence();
      } catch (e) {
        console.warn('⚠️ Persistencia auth:', e);
      }

      try {
        const sso = await applyEdusicoSsoFromUrl();
        if (sso.applied) {
          console.log('✅ Sesión Edusico aplicada en esta app:', sso.method);
          clearBordonaSessionCache();
        }
      } catch (e) {
        console.warn('⚠️ SSO desde Edusico no aplicado:', e);
      }

      if (cancelled) return;

      unsubscribe = authService.onAuthStateChange(async (authUser) => {
      if (authUser) {
        const prevUid = previousUidRef.current;
        if (prevUid && prevUid !== authUser.uid) {
          clearBordonaSessionCache();
        }
        previousUidRef.current = authUser.uid;

        console.log('✅ Usuario autenticado:', authUser.email);
        if (typeof window !== 'undefined' && window?.localStorage?.getItem('DEBUG_FIREBASE') === '1') {
          console.log('🔐 UID autenticado:', authUser.uid);
        }

        ensureUserStorageWorkspace(authUser.uid);

        // Crear/verificar perfil de acceso (lista de espera)
        try {
          const profile = await checkUserAccess(authUser);
          setAccessProfile(profile);
        } catch (e) {
          console.error('❌ Error verificando accessStatus:', e);
          setAccessProfile(null);
        }

        // Cargar datos del profesor desde Firestore
        try {
          const profData = await authService.getProfesorData(authUser.uid);
          console.log('📚 Datos del profesor cargados:', profData);

          setUser(authUser);
          setProfesorData(profData);
        } catch (error) {
          console.error('❌ Error al cargar datos del profesor:', error);
          setUser(authUser);
          setProfesorData(null);
        }
      } else {
        console.log('❌ Usuario no autenticado');
        previousUidRef.current = null;
        setUser(null);
        setAccessProfile(null);
        setProfesorData(null);
        clearLegacyProfesorId();
        try { localStorage.removeItem('profesorIdContext'); } catch {}
      }
      if (!cancelled) setLoading(false);
    });
    };

    boot();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /**
   * 📧 Login con email y contraseña
   */
  const login = async (email, password) => {
    try {
      setError(null);
      console.log('📧 Intentando login con:', email);
      const userData = await authService.login(email, password);
      console.log('✅ Login exitoso, usuario:', userData.email);
      
      // Los datos se cargarán automáticamente por onAuthStateChange
      return userData;
    } catch (err) {
      console.error('❌ Error en login:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * 🔍 Login con Google
   */
  const loginWithGoogle = async () => {
    try {
      setError(null);
      console.log('🔍 Intentando login con Google...');
      const user = await authService.loginWithGoogle();
      console.log('✅ Login con Google exitoso:', user.email);
      
      // Los datos se cargarán automáticamente por onAuthStateChange
      return user;
    } catch (err) {
      console.error('❌ Error en login con Google:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * 📝 Registro de nuevo usuario
   */
  const register = async (userData) => {
    try {
      setError(null);
      console.log('📝 Registrando nuevo usuario:', userData.email);
      const user = await authService.register(userData);
      console.log('✅ Registro exitoso:', user.email);
      
      // Los datos se cargarán automáticamente por onAuthStateChange
      return user;
    } catch (err) {
      console.error('❌ Error en registro:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * 🚪 Cerrar sesión
   */
  const logout = async () => {
    try {
      setError(null);
      await authService.logout();
      setUser(null);
      setProfesorData(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * 🔄 Recuperar contraseña
   */
  const resetPassword = async (email) => {
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * 👤 Actualizar perfil
   */
  const updateProfile = async (profileData) => {
    try {
      setError(null);
      await authService.updateUserProfile(profileData);
      
      // Actualizar estado local
      const updatedProfesorData = await authService.getProfesorData(user.uid);
      setProfesorData(updatedProfesorData);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * 🔄 Recargar datos del profesor
   */
  const reloadProfesorData = async () => {
    if (user) {
      const updatedData = await authService.getProfesorData(user.uid);
      setProfesorData(updatedData);
    }
  };

  const value = {
    // Estado
    user,
    accessProfile,
    profesorData,
    loading,
    error,
    isAuthenticated: !!user,
    profesorId: user?.uid || null,

    accessStatus: accessProfile?.accessStatus || null,
    role: accessProfile?.role || null,
    accountType: accessProfile?.accountType || 'profesor',
    isAdmin: accessProfile?.role === 'admin',
    
    // Funciones
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updateProfile,
    reloadProfesorData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 🪝 Hook para usar el contexto de autenticación
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
